import { RoomModel } from './room.model';
import { RoleModel } from './role.model';
import { ObserverModel } from './observer.model';
import { CandidateModel } from './candidate.model';
import { Deserializable } from './deserializable';
import { UserModel } from './user.model';
import { TemplateModel } from './template.model';
import { STATUS_NAMES } from '../constants';
import { LocationModel } from './location.model';
import { LogentryModel } from './logentry.model';
import { CustomerModel } from './customer.model';
import { TodoModel } from './todo.model';

export class AcModel extends TemplateModel implements Deserializable {

  // columns
  location_name: string; // tslint:disable-line: variable-name
  location_other: any; // tslint:disable-line: variable-name
  start_time: any; // tslint:disable-line: variable-name
  start_time_admin: any; // tslint:disable-line: variable-name
  started_at: any; // tslint:disable-line: variable-name
  finished_at: any; // tslint:disable-line: variable-name
  cancelled_at: any; // tslint:disable-line: variable-name
  duration: number;
  conference_url: string; // tslint:disable-line: variable-name
  conference_info: string; // tslint:disable-line: variable-name
  prebriefing_url: string; // tslint:disable-line: variable-name
  prebriefing_info: string; // tslint:disable-line: variable-name
  informations_internal: string; // tslint:disable-line: variable-name
  informations_moderator: string; // tslint:disable-line: variable-name
  country: string;
  timezone: string;
  misc: any;
  clearing: boolean;
  clearing_info: any; // tslint:disable-line: variable-name
  ordernumber: number;
  logs?: Array<LogentryModel> = [];
  movable: boolean;
  finishable: boolean;

  // orders
  dispatcher_id: number; // tslint:disable-line: variable-name
  location_id: number; // tslint:disable-line: variable-name
  template_id: number; // tslint:disable-line: variable-name
  businessunit_id?: number; // tslint:disable-line: variable-name

  // relations
  template: TemplateModel = null;
  moderator: UserModel = null;
  customer: CustomerModel = null;
  dispatcher: UserModel = null;
  orderer: UserModel = null;
  hiringmanager: UserModel = null;
  candidates: Array<CandidateModel> = [];
  observers: Array<ObserverModel> = [];
  users: Array<UserModel> = [];
  location: LocationModel = null;
  rooms: Array<RoomModel> = [];

  deserialize(input: any): this {
    Object.assign(this, super.deserialize(input));
    // template
    if (this.template !== null && this.template !== undefined) {
      this.template = new TemplateModel().deserialize(this.template);
    }
    // moderator
    if (this.moderator !== null && this.moderator !== undefined) {
      this.moderator = new UserModel().deserialize(this.moderator);
    }
    // customer
    if (this.customer !== null && this.customer !== undefined) {
      this.customer = new CustomerModel().deserialize(this.customer);
    }
    // dispatcher
    if (this.dispatcher !== null && this.dispatcher !== undefined) {
      this.dispatcher = new UserModel().deserialize(this.dispatcher);
    }
    // orderer
    if (this.orderer !== null && this.orderer !== undefined) {
      this.orderer = new UserModel().deserialize(this.orderer);
    }
    // room
    let rooms = null;
    if (this.rooms !== null && this.rooms !== undefined) {
      rooms = [];
      this.rooms.forEach((room: RoomModel) => {
        rooms.push(new RoomModel().deserialize(room));
      });
    }
    this.rooms = rooms;
    // location
    if (this.location !== null && this.location !== undefined) {
      this.location = new LocationModel().deserialize(this.location);
    }
    // candidates
    let candidates = null;
    if (this.candidates !== null && this.candidates !== undefined) {
      candidates = [];
      this.candidates.forEach((candidate: CandidateModel) => {
        candidates.push(new CandidateModel().deserialize(candidate));
      });
    }
    this.candidates = candidates;
    // observers
    let observers = null;
    if (this.observers !== null && this.observers !== undefined) {
      observers = [];
      this.observers.forEach((observer: ObserverModel) => {
        observers.push(new ObserverModel().deserialize(observer));
      });
    }
    this.observers = observers;
    // users
    let users = null;
    if (this.users !== null && this.users !== undefined) {
      users = [];
      this.users.forEach((user: UserModel) => {
        users.push(new UserModel().deserialize(user));
      });
    }
    this.users = users;
    // logentries
    let logs = null;
    if (this.logs !== null && this.logs !== undefined) {
      logs = [];
      this.logs.forEach((log: any) => {
        logs.push(new LogentryModel().deserialize(log));
      });
    }
    this.logs = logs;
    // hiring manager
    const hrs = this.getUsersByRole('hiringmanager');
    if (hrs.length > 0) {
      this.hiringmanager = hrs[0];
    }
    // todos
    this.todos = this.todos?.map((todo: any) => new TodoModel().deserialize(todo)) || null;
    return this;
  }

  public get translatedStatusName(): string {
    return this.transloco?.translate(STATUS_NAMES[this.status]) || STATUS_NAMES[this.status];
  }

  public getStatusName(): string {
    return STATUS_NAMES[this.status];
  }

  public isCancellable(): boolean {
    return this.status === 'requested' || this.status === 'planned';
  }

  public isCancelled(): boolean {
    return this.status === 'cancelled';
  }

  public isOrderable(): boolean {
    return this.status === 'requested' &&
      (this.clearing !== true || this.clearing_info.missing === null || (Array.isArray(this.clearing_info.missing) && this.clearing_info.missing.length === 0));
  }

  public invitationSended(): boolean {
    return this.status !== 'requested';
  }

  public getUsersByRole(roleName: string): Array<UserModel> {
    return this.users.filter((user: UserModel) => {
      return user.roles.find((role: RoleModel) => {
        return role.name === roleName;
      }) !== undefined;
    });
  }

  public hasCandidate(): boolean {
    return this.candidates?.length > 0;
  }

  public hasRooms(): boolean {
    return this.rooms.length > 1 || !!this.location_other?.room_main === true;
  }

  public getMainRoom(): RoomModel | null {
    return this.rooms?.find((room: RoomModel) => {
      return room.type === 'main';
    }) || null;
  }

  public getPreparationRoom(): RoomModel | null {
    return this.rooms?.find((room: RoomModel) => {
      return room.type === 'preparation';
    }) || null;
  }

  public getMainRoomName(): string {
    if (this.rooms.length > 1) {
      return this.rooms[0]?.name;
    } else if (this.location_other?.room_main) {
      return this.location_other.room_main;
    }
  }

  public get status_with_requested_as_planned(): string {
    if (this.status === 'requested') {
      return 'planned';
    }
    return this.status;
  }

  public get locationName(): string {
    if (this.location) {
      return this.location.name;
    } else if (this.location_other?.city) {
      return this.location_other.city;
    }
    return this.location_name;
  }

  public get location_type(): string {
    if (this.onsite === true) {
      return this.transloco?.translate('common.labels.onsite') || 'onsite';
    }
    return this.transloco?.translate('common.labels.virtual') || 'virtual';
  }

  public get not_yet_started(): boolean {
    return this.status === 'requested' || this.status === 'planned';
  }

  public get moderator_name(): string {
    return (this.moderator !== null) ? this.moderator.fullname : '';
  }

  public get customer_name(): string {
    return (this.customer !== null) ? this.customer.name : '';
  }

  public get dispatcher_name(): string {
    return (this.dispatcher !== null) ? this.dispatcher.fullname : '';
  }

  public get orderer_name(): string {
    return (this.orderer !== null) ? this.orderer.fullname : '';
  }

  public get hiringmanager_name(): string {
    return (this.hiringmanager) ? this.hiringmanager.fullname : '';
  }

  public get room_name(): string {
    if (Array.isArray(this.rooms) && this.rooms.length > 0) {
      const rooms = this.rooms.filter((room: RoomModel) => {
        return room.type === 'main';
      });
      if (rooms.length > 0) {
        return rooms[0].name;
      } else {
        return this.rooms[0].name;
      }
    }
    return '';
  }

  public get observer_names(): string {
    const parts: Array<string> = [];
    if (Array.isArray(this.observers) && this.observers.length > 0) {
      this.observers.forEach((observer: ObserverModel) => {
        parts.push(observer.firstname + ' ' + observer.lastname);
      });
    }
    return parts.join(', ');
  }

  public get candidate_names(): string {
    const parts: Array<string> = [];
    if (Array.isArray(this.candidates) && this.candidates.length > 0) {
      this.candidates.forEach((candidate: CandidateModel) => {
        if (candidate.firstname || candidate.lastname) {
          parts.push(candidate.firstname + ' ' + candidate.lastname);
        }
      });
    }
    return parts.join(', ');
  }

  public get start_time_as_date(): Date {
    return new Date(this.start_time);
  }

  // todos

  public hasTodos(): boolean {
    return this.todos?.length > 0;
  }

}
